/**
 * Operations calls (docs/specs/operations.md): Compliance (the OC board, branch and region
 * pages, live drill-downs, KPI thresholds and settings) and Announcements (authoring, audience
 * preview, publish/withdraw, the read/acknowledge report, and the reader's own list). The API
 * narrows the board to the viewer's scope and sanitises announcement HTML.
 */
import {
  AnnouncementAdminSchema,
  AnnouncementReportSchema,
  AnnouncementsAdminResponseSchema,
  AnnouncementViewSchema,
  AudienceOptionsSchema,
  AudiencePreviewSchema,
  MyAnnouncementsSchema,
  OcBoardSchema,
  OcBranchDetailSchema,
  OcDrillDownSchema,
  OcKpiSchema,
  OcKpisResponseSchema,
  OcRegionDetailSchema,
  OcSettingsSchema,
  OperationsOkSchema,
  type Audience,
  type AnnouncementWrite,
  type OcKpiPatch,
  type OcSettingsPatch,
} from '@aroo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, apiUpload } from './client';

const enc = encodeURIComponent;

export const operationsKeys = {
  compliance: ['operations', 'compliance'] as const,
  board: ['operations', 'compliance', 'board'] as const,
  branch: (siteId: string, days: number) =>
    ['operations', 'compliance', 'branch', siteId, days] as const,
  drill: (siteId: string, code: string) =>
    ['operations', 'compliance', 'drill', siteId, code] as const,
  region: (regionId: string, days: number) =>
    ['operations', 'compliance', 'region', regionId, days] as const,
  kpis: ['operations', 'compliance', 'kpis'] as const,
  settings: ['operations', 'compliance', 'settings'] as const,
  announcements: ['operations', 'announcements'] as const,
  announcement: (id: string) => ['operations', 'announcements', id] as const,
  report: (id: string) => ['operations', 'announcements', id, 'report'] as const,
  options: ['operations', 'announcements', 'options'] as const,
  preview: (audience: Audience) => ['operations', 'announcements', 'preview', audience] as const,
  mine: ['announcements', 'mine'] as const,
  view: (id: string) => ['announcements', 'view', id] as const,
};

/** Paths for CSV downloads (plain links: the session cookie rides along). */
export const operationsCsv = {
  drill: (siteId: string, code: string) =>
    `/api/v1/operations/compliance/branches/${enc(siteId)}/kpis/${enc(code)}/csv`,
  report: (id: string) => `/api/v1/operations/announcements/${enc(id)}/report/csv`,
};

// Compliance -------------------------------------------------------------------------

export function useOcBoard(enabled = true) {
  return useQuery({
    queryKey: operationsKeys.board,
    queryFn: () => apiGet('/operations/compliance/board', OcBoardSchema),
    refetchInterval: 5 * 60_000,
    enabled,
  });
}

export function useOcBranch(siteId: string, days: number) {
  return useQuery({
    queryKey: operationsKeys.branch(siteId, days),
    queryFn: () =>
      apiGet(`/operations/compliance/branches/${enc(siteId)}`, OcBranchDetailSchema, { days }),
    enabled: !!siteId,
  });
}

export function useOcDrill(siteId: string, code: string | null) {
  return useQuery({
    queryKey: operationsKeys.drill(siteId, code ?? ''),
    queryFn: () =>
      apiGet(
        `/operations/compliance/branches/${enc(siteId)}/kpis/${enc(code ?? '')}`,
        OcDrillDownSchema,
      ),
    enabled: !!siteId && !!code,
  });
}

export function useOcRegion(regionId: string, days: number) {
  return useQuery({
    queryKey: operationsKeys.region(regionId, days),
    queryFn: () =>
      apiGet(`/operations/compliance/regions/${enc(regionId)}`, OcRegionDetailSchema, { days }),
    enabled: !!regionId,
  });
}

export function useOcKpis() {
  return useQuery({
    queryKey: operationsKeys.kpis,
    queryFn: () => apiGet('/operations/compliance/kpis', OcKpisResponseSchema),
  });
}

export function useOcSettings(enabled = true) {
  return useQuery({
    queryKey: operationsKeys.settings,
    queryFn: () => apiGet('/operations/compliance/settings', OcSettingsSchema),
    enabled,
  });
}

function useInvalidateCompliance() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: operationsKeys.compliance });
}

export function usePatchOcKpi() {
  const invalidate = useInvalidateCompliance();
  return useMutation({
    mutationFn: ({ code, patch }: { code: string; patch: OcKpiPatch }) =>
      apiPatch(`/operations/compliance/kpis/${enc(code)}`, OcKpiSchema, patch),
    onSuccess: () => invalidate(),
  });
}

export function usePatchOcSettings() {
  const invalidate = useInvalidateCompliance();
  return useMutation({
    mutationFn: (patch: OcSettingsPatch) =>
      apiPatch('/operations/compliance/settings', OcSettingsSchema, patch),
    onSuccess: () => invalidate(),
  });
}

// Announcements: authoring -------------------------------------------------------------

export function useAnnouncementsAdmin() {
  return useQuery({
    queryKey: operationsKeys.announcements,
    queryFn: () => apiGet('/operations/announcements', AnnouncementsAdminResponseSchema),
  });
}

export function useAnnouncementAdmin(id: string | null) {
  return useQuery({
    queryKey: operationsKeys.announcement(id ?? ''),
    queryFn: () => apiGet(`/operations/announcements/${enc(id ?? '')}`, AnnouncementAdminSchema),
    enabled: !!id,
  });
}

export function useAudienceOptions() {
  return useQuery({
    queryKey: operationsKeys.options,
    queryFn: () => apiGet('/operations/announcements/audience-options', AudienceOptionsSchema),
    staleTime: 5 * 60_000,
  });
}

export function useAudiencePreview(audience: Audience | null) {
  return useQuery({
    queryKey: operationsKeys.preview(audience ?? EMPTY_AUDIENCE),
    queryFn: () =>
      apiPost('/operations/announcements/audience-preview', AudiencePreviewSchema, { audience }),
    enabled: !!audience,
    placeholderData: (prev) => prev,
  });
}

export const EMPTY_AUDIENCE: Audience = {
  everyone: false,
  divisionIds: [],
  regionIds: [],
  siteIds: [],
  positionKinds: [],
  roleCodes: [],
};

export function useAnnouncementReport(id: string | null, enabled = true) {
  return useQuery({
    queryKey: operationsKeys.report(id ?? ''),
    queryFn: () =>
      apiGet(`/operations/announcements/${enc(id ?? '')}/report`, AnnouncementReportSchema),
    enabled: !!id && enabled,
  });
}

function useInvalidateAnnouncements() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: operationsKeys.announcements }),
      qc.invalidateQueries({ queryKey: operationsKeys.mine }),
      qc.invalidateQueries({ queryKey: ['notifications'] }),
    ]);
}

export function useCreateAnnouncement() {
  const invalidate = useInvalidateAnnouncements();
  return useMutation({
    mutationFn: (body: AnnouncementWrite) =>
      apiPost('/operations/announcements', AnnouncementAdminSchema, body),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateAnnouncement() {
  const invalidate = useInvalidateAnnouncements();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AnnouncementWrite }) =>
      apiPatch(`/operations/announcements/${enc(id)}`, AnnouncementAdminSchema, body),
    onSuccess: () => invalidate(),
  });
}

export function useAnnouncementStep() {
  const invalidate = useInvalidateAnnouncements();
  return useMutation({
    mutationFn: ({ id, step }: { id: string; step: 'publish' | 'withdraw' }) =>
      apiPost(`/operations/announcements/${enc(id)}/${step}`, AnnouncementAdminSchema),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteAnnouncement() {
  const invalidate = useInvalidateAnnouncements();
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete(`/operations/announcements/${enc(id)}`, OperationsOkSchema),
    onSuccess: () => invalidate(),
  });
}

export function useAnnouncementFile() {
  const invalidate = useInvalidateAnnouncements();
  return useMutation({
    mutationFn: ({
      id,
      kind,
      file,
    }: {
      id: string;
      kind: 'image' | 'attachment';
      file: File | null;
    }) => {
      if (!file) {
        return apiDelete(
          `/operations/announcements/${enc(id)}/files/${kind}`,
          AnnouncementAdminSchema,
        );
      }
      // The field first, then the file (the API reads `kind` before storing any bytes).
      const form = new FormData();
      form.append('kind', kind);
      form.append('file', file);
      return apiUpload(`/operations/announcements/${enc(id)}/files`, AnnouncementAdminSchema, form);
    },
    onSuccess: () => invalidate(),
  });
}

// Announcements: reading ---------------------------------------------------------------

/** My live announcements (Home card, shell banner). No polling: refetched on focus. */
export function useMyAnnouncements(enabled = true) {
  return useQuery({
    queryKey: operationsKeys.mine,
    queryFn: () => apiGet('/announcements/mine', MyAnnouncementsSchema),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    enabled,
  });
}

export function useAnnouncementView(id: string) {
  return useQuery({
    queryKey: operationsKeys.view(id),
    queryFn: () => apiGet(`/announcements/${enc(id)}`, AnnouncementViewSchema),
    enabled: !!id,
  });
}

export function useAnnouncementReadAck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, step }: { id: string; step: 'read' | 'ack' }) =>
      apiPost(`/announcements/${enc(id)}/${step}`, AnnouncementViewSchema),
    onSuccess: (view) => {
      qc.setQueryData(operationsKeys.view(view.id), view);
      void qc.invalidateQueries({ queryKey: operationsKeys.mine });
    },
  });
}
